import { useState, type VoidFunctionComponent } from 'react';
import { Modal } from 'react-bootstrap';

import { QualityDisputeReasonLabels } from '../../../shared/model-typings/qualitydisputes';
import useInputValue from '../../hooks/useInputValue';
import { selectOptionFactory } from '../../utils';
import Button from '../generic/Button/Button';
import ButtonGroup from '../generic/Button/ButtonGroup';
import LoaderButton from '../generic/Button/LoaderButton';
import FilterCardRadios from '../generic/Filter/FilterCardRadios';
import FormGroup from '../generic/Forms/FormGroup';
import TextArea from '../generic/Forms/TextArea';
import Text from '../generic/Text';
import type {
  QualityDisputeReasonsKey,
  QualityDisputeReasonsType,
} from '../../../shared/model-typings/qualitydisputes';

type Props = {
  isPending: boolean;
  show: boolean;
  onHide: () => void;
  onSubmit: (comment: string, reason: QualityDisputeReasonsType) => void;
};

const [defaultDisputeReason] = Object.keys(QualityDisputeReasonLabels) as [QualityDisputeReasonsKey];

const disputeReasonOptions = Object.entries(QualityDisputeReasonLabels)
  .map(([value, label]) => selectOptionFactory<QualityDisputeReasonsKey>(value as QualityDisputeReasonsKey, label));

export const ModalReportQualityDispute: VoidFunctionComponent<Props> = ({
  isPending,
  show,
  onHide,
  onSubmit,
}) => {
  const [reason, setReason] = useState<QualityDisputeReasonsKey>(defaultDisputeReason);
  const [comment, onCommentChange] = useInputValue('');

  const handleSubmit = async () => {
    if (isPending) {
      return;
    }

    await onSubmit(comment, reason);
    onCommentChange('');
    setReason(defaultDisputeReason);
  };

  return (
    <Modal
      show={show}
      onHide={onHide}
    >
      <Modal.Header>
        <Text.H2>
          Quality Feedback
        </Text.H2>
      </Modal.Header>
      <Modal.Body>
        <p className="margin-bottom-sm">
          Your feedback will be reviewed by the FOSSA team to help improve our quality analysis experience.
        </p>
        <FormGroup>
          <FilterCardRadios
            label="What is the problem with this quality issue?"
            options={disputeReasonOptions}
            value={reason}
            onChange={setReason}
            isDisabled={isPending}
          />
          <TextArea
            label="Dispute comment"
            placeholder="Give us any extra information you think can be useful."
            value={comment}
            onChange={onCommentChange}
            isDisabled={isPending}
            isRequired
          />
        </FormGroup>
      </Modal.Body>
      <Modal.Footer>
        <ButtonGroup direction="right">
          <Button
            type="secondary"
            size="medium"
            onClick={onHide}
            isDisabled={isPending}
          >
            Cancel
          </Button>
          <LoaderButton
            type="primary"
            size="medium"
            onClick={handleSubmit}
            isDisabled={isPending}
            isLoading={isPending}
          >
            Submit Feedback
          </LoaderButton>
        </ButtonGroup>
      </Modal.Footer>
    </Modal>
  );
};
